(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const desktopPointerQuery = window.matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)');
  const controlHideDelay = 1100;
  const isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
  const copy = isSpanish ? {
    play: 'Reproducir video del producto',
    pause: 'Pausar video del producto',
    dialog: 'Video del producto',
    close: 'Cerrar video del producto',
  } : {
    play: 'Play product video',
    pause: 'Pause product video',
    dialog: 'Product video',
    close: 'Close product video',
  };

  const init = (root) => {
    if (!root || root.dataset.yxProductVideoReady === 'true') return;
    root.dataset.yxProductVideoReady = 'true';

    root.querySelectorAll('[data-yx-video-wrap]').forEach((wrap) => {
      const video = wrap.querySelector('video');
      const play = wrap.querySelector('[data-yx-video-play]');
      if (!video || !play) return;

      let controlsTimer = 0;
      let dialog = null;
      let dialogSlot = null;
      let placeholder = null;
      let lastTouchActivation = -Infinity;

      const setControlsVisible = (visible) => {
        wrap.classList.toggle('is-controls-visible', visible);
      };

      const scheduleControlsHide = () => {
        window.clearTimeout(controlsTimer);
        if (video.paused || video.ended) return;
        controlsTimer = window.setTimeout(() => setControlsVisible(false), controlHideDelay);
      };

      const revealControls = () => {
        if (video.paused || video.ended) return;
        setControlsVisible(true);
        scheduleControlsHide();
      };

      const getRenderedMediaRect = () => {
        const rect = video.getBoundingClientRect();
        const fit = window.getComputedStyle(video).objectFit;
        if (fit !== 'contain' || !video.videoWidth || !video.videoHeight) return rect;

        const mediaRatio = video.videoWidth / video.videoHeight;
        const boxRatio = rect.width / rect.height;
        const width = mediaRatio > boxRatio ? rect.width : rect.height * mediaRatio;
        const height = mediaRatio > boxRatio ? rect.width / mediaRatio : rect.height;
        return {
          left: rect.left + (rect.width - width) / 2,
          right: rect.left + (rect.width + width) / 2,
          top: rect.top + (rect.height - height) / 2,
          bottom: rect.top + (rect.height + height) / 2,
        };
      };

      const handlePointerMove = (event) => {
        if (!desktopPointerQuery.matches) {
          revealControls();
          return;
        }

        const mediaRect = getRenderedMediaRect();
        const isOverMedia = event.clientX >= mediaRect.left
          && event.clientX <= mediaRect.right
          && event.clientY >= mediaRect.top
          && event.clientY <= mediaRect.bottom;
        wrap.classList.toggle('is-media-hovered', isOverMedia);
        if (isOverMedia) revealControls();
        else {
          window.clearTimeout(controlsTimer);
          setControlsVisible(false);
        }
      };

      const syncControl = () => {
        const isPlaying = !video.paused && !video.ended;
        wrap.classList.toggle('is-playing', isPlaying);
        play.setAttribute('aria-label', isPlaying ? copy.pause : copy.play);
        play.setAttribute('aria-pressed', String(isPlaying));
        if (isPlaying) scheduleControlsHide();
        else {
          window.clearTimeout(controlsTimer);
          setControlsVisible(true);
        }
      };

      const flagOrientation = () => {
        if (!video.videoWidth || !video.videoHeight) return;
        const isPortrait = video.videoHeight > video.videoWidth;
        wrap.classList.toggle('is-portrait', isPortrait);
        wrap.dataset.yxVideoOrientation = isPortrait ? 'portrait' : 'landscape';
      };

      const sizePopupVideo = () => {
        if (!dialog?.open || !video.videoWidth || !video.videoHeight) return;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const maxWidth = Math.max(240, window.innerWidth - 32);
        const maxHeight = Math.max(320, viewportHeight - 72);
        const ratio = video.videoWidth / video.videoHeight;
        let width = maxWidth;
        let height = width / ratio;
        if (height > maxHeight) {
          height = maxHeight;
          width = height * ratio;
        }
        wrap.style.setProperty('--yx-video-popup-width', `${Math.round(width)}px`);
        wrap.style.setProperty('--yx-video-popup-height', `${Math.round(height)}px`);
      };

      const restoreInlineVideo = () => {
        if (placeholder?.isConnected) placeholder.replaceWith(wrap);
        placeholder = null;
        wrap.classList.remove('is-popup');
        wrap.style.removeProperty('--yx-video-popup-width');
        wrap.style.removeProperty('--yx-video-popup-height');
        document.documentElement.classList.remove('yx-product-video-modal-open');
      };

      const closePopup = () => {
        if (!dialog?.open) return;
        video.pause();
        dialog.close();
      };

      const ensureDialog = () => {
        if (dialog) return dialog;
        dialog = document.createElement('dialog');
        dialog.className = 'yx-product-video-dialog';
        dialog.setAttribute('aria-label', copy.dialog);
        dialog.innerHTML = `
          <div class="yx-product-video-dialog__shell">
            <button class="yx-product-video-dialog__close" type="button" aria-label="${copy.close}"></button>
            <div class="yx-product-video-dialog__slot"></div>
          </div>
        `;
        dialogSlot = dialog.querySelector('.yx-product-video-dialog__slot');
        dialog.querySelector('.yx-product-video-dialog__close').addEventListener('click', closePopup);
        dialog.addEventListener('click', (event) => {
          if (
            event.target === dialog
            || event.target.classList.contains('yx-product-video-dialog__shell')
            || event.target.classList.contains('yx-product-video-dialog__slot')
          ) closePopup();
        });
        dialog.addEventListener('cancel', () => video.pause());
        dialog.addEventListener('close', restoreInlineVideo);
        document.body.append(dialog);
        return dialog;
      };

      const openPortraitPopup = () => {
        const popup = ensureDialog();
        placeholder = document.createElement('div');
        placeholder.className = 'yx-product-video-placeholder';
        placeholder.style.height = `${wrap.offsetHeight}px`;
        wrap.before(placeholder);
        dialogSlot.append(wrap);
        wrap.classList.add('is-popup');
        document.documentElement.classList.add('yx-product-video-modal-open');
        popup.showModal();
        sizePopupVideo();
      };

      if (video.readyState >= 1) flagOrientation();
      video.addEventListener('loadedmetadata', flagOrientation);
      video.addEventListener('loadeddata', flagOrientation);

      const togglePlayback = () => {
        const shouldOpenPopup = mobileQuery.matches && wrap.classList.contains('is-portrait') && !dialog?.open;
        if (shouldOpenPopup) openPortraitPopup();
        if (video.paused || video.ended) {
          video.removeAttribute('controls');
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      };

      play.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' || event.button !== 0) return;
        lastTouchActivation = window.performance.now();
        togglePlayback();
        play.blur();
      });
      play.addEventListener('click', (event) => {
        /* Touch browsers normally synthesize click after pointerup. Ignore
           that duplicate while keeping mouse and keyboard activation native. */
        if (event.detail !== 0 && window.performance.now() - lastTouchActivation < 700) {
          event.preventDefault();
          return;
        }
        togglePlayback();
        /* Pointer activation must not leave :focus-visible pinning the control
           over the playing frame. Keyboard activation keeps focus so Pause
           remains reachable. */
        if (event.detail !== 0) play.blur();
      });
      video.addEventListener('pointerdown', revealControls);
      video.addEventListener('touchstart', revealControls, { passive: true });
      video.addEventListener('click', revealControls);
      wrap.addEventListener('pointermove', handlePointerMove);
      wrap.addEventListener('pointerleave', () => {
        wrap.classList.remove('is-media-hovered');
        if (!video.paused && !video.ended && !wrap.contains(document.activeElement)) setControlsVisible(false);
      });
      wrap.addEventListener('focusin', () => setControlsVisible(true));
      wrap.addEventListener('focusout', scheduleControlsHide);
      video.addEventListener('pause', () => {
        syncControl();
      });
      video.addEventListener('ended', () => {
        syncControl();
      });
      video.addEventListener('play', () => {
        syncControl();
      });
      window.addEventListener('resize', sizePopupVideo);
      window.visualViewport?.addEventListener('resize', sizePopupVideo);
      mobileQuery.addEventListener('change', (event) => {
        if (!event.matches) closePopup();
      });
      syncControl();
    });
  };

  window.YurinoxPdpProductVideo = { init };
  document.querySelectorAll('[data-yx-product-root]').forEach(init);
})();
